import React, { useContext, useState, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import api from '../api';
import './CSS/Payment.css';
import { ShopContext } from '../Context/ShopContext';

const Payment = () => {
    const { all_Product } = useContext(ShopContext);
    const location = useLocation();
    const { cartItems, totalAmount = 0, subtotal = 0, discount = 0, deliveryFee = 100 } = location.state || {};

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Ambassador code state
    const [ambassadorCode, setAmbassadorCode] = useState('');
    const [ambassadorInput, setAmbassadorInput] = useState('');
    const [ambassadorDiscount, setAmbassadorDiscount] = useState(0);
    const [ambassadorName, setAmbassadorName] = useState('');
    const [ambassadorError, setAmbassadorError] = useState('');
    const [ambassadorLoading, setAmbassadorLoading] = useState(false);
    const [ambassadorApplied, setAmbassadorApplied] = useState(false);

    // --- THIS LINE IS THE FIX ---
    // The 'street: ""' key has been removed from the initial state.
    const [deliveryInfo, setDeliveryInfo] = useState({
        name: '', surname: '', email: '', addressLine: '', Appartment: '',
        Province: '', city: '', postalCode: '',
    });

    const filteredCartItems = useMemo(() =>
        Object.keys(cartItems || {})
            .filter((id) => cartItems[id]?.quantity > 0)
            .reduce((obj, key) => {
                const product = all_Product.find((p) => p.id === Number(key));
                if (product) {
                    obj[key] = {
                        ...cartItems[key],
                        name: product.name || 'Unknown Product',
                        image: product.image || 'default-image.jpg',
                        price: product.new_price || 0,
                    };
                }
                return obj;
            }, {}),
        [cartItems, all_Product]
    );

    // Compute totals accounting for ambassador discount
    const finalAmbassadorDiscount = ambassadorDiscount;
    const finalTotal = Math.max(0, subtotal - discount - finalAmbassadorDiscount + deliveryFee);

    const applyAmbassadorCode = async () => {
        setAmbassadorError('');
        if (!ambassadorInput.trim()) {
            setAmbassadorError('Please enter a code.');
            return;
        }
        setAmbassadorLoading(true);
        try {
            const res = await api.post('/validate-ambassador-code', { code: ambassadorInput.trim() });
            if (res.data.success) {
                const discAmt = Math.round(subtotal * (res.data.discountPct / 100) * 100) / 100;
                setAmbassadorDiscount(discAmt);
                setAmbassadorName(res.data.ambassadorName);
                setAmbassadorCode(ambassadorInput.trim().toUpperCase());
                setAmbassadorApplied(true);
            } else {
                setAmbassadorError('Invalid code. Please try again.');
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setAmbassadorError('Invalid ambassador code.');
            } else {
                setAmbassadorError('Could not verify code. Please try again.');
            }
        } finally {
            setAmbassadorLoading(false);
        }
    };

    const removeAmbassadorCode = () => {
        setAmbassadorCode('');
        setAmbassadorInput('');
        setAmbassadorDiscount(0);
        setAmbassadorName('');
        setAmbassadorApplied(false);
        setAmbassadorError('');
    };

    const handleInputChange = (e) => {
        setDeliveryInfo({ ...deliveryInfo, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setError(null);

        // This validation will now work correctly.
        for (const key in deliveryInfo) {
            if (key !== 'Appartment' && deliveryInfo[key] === '') {
                setError('Please fill out all required delivery information fields.');
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const response = await api.post('/create-checkout', {
                amountInCents: finalTotal * 100,
                subtotal: subtotal,
                discount: discount,
                deliveryFee: deliveryFee,
                deliveryInfo,
                cartItems: filteredCartItems,
                ambassadorCode: ambassadorCode || undefined,
            });

            if (response.data.success && response.data.redirectUrl) {
                window.location.href = response.data.redirectUrl;
            } else {
                throw new Error(response.data.message || 'Failed to retrieve redirect URL.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Could not initialize payment. Please try again.');
            setIsSubmitting(false);
        }
    };

    const [isSummaryOpen, setIsSummaryOpen] = useState(false);

    return (
        <div className="checkout-page container">
            <form id="payment-form" className="checkout-layout" onSubmit={handleSubmit}>
                <div className="checkout-main">
                    <div className="checkout-logo">
                        <Link to="/">SNEAKER PRODUCTION CO</Link>
                    </div>
                    <div className="checkout-breadcrumb">
                        <Link to="/cart">Cart</Link> <span>{'>'}</span> Information <span>{'>'}</span> Payment
                    </div>
                    <section className="checkout-section">
                        <h2>Delivery Information</h2>
                        <div className="form-grid">
                            <div className="form-group half-width">
                                <label htmlFor="name">Name</label>
                                <input id="name" type="text" name="name" value={deliveryInfo.name} onChange={handleInputChange} required autoComplete="given-name" />
                            </div>
                            <div className="form-group half-width">
                                <label htmlFor="surname">Surname</label>
                                <input id="surname" type="text" name="surname" value={deliveryInfo.surname} onChange={handleInputChange} required autoComplete="family-name" />
                            </div>
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input id="email" type="email" name="email" value={deliveryInfo.email} onChange={handleInputChange} required autoComplete="email" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="addressLine">Address</label>
                            <input id="addressLine" type="text" name="addressLine" placeholder="Street Address" value={deliveryInfo.addressLine} onChange={handleInputChange} required autoComplete="street-address" />
                        </div>
                        <div className="form-group">
                            <input type="text" name="Appartment" placeholder="Apartment, suite, etc. (optional)" value={deliveryInfo.Appartment} onChange={handleInputChange} autoComplete="address-line2" />
                        </div>
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="city">City</label>
                                <input id="city" type="text" name="city" value={deliveryInfo.city} onChange={handleInputChange} required autoComplete="address-level2" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="Province">Province</label>
                                <input id="Province" type="text" name="Province" value={deliveryInfo.Province} onChange={handleInputChange} required autoComplete="address-level1" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="postalCode">Postal Code</label>
                                <input id="postalCode" type="text" name="postalCode" value={deliveryInfo.postalCode} onChange={handleInputChange} required autoComplete="postal-code" />
                            </div>
                        </div>
                    </section>
                    <section className="checkout-section payment-details">
                        <h2>Payment</h2>
                        <p>You will be redirected to Yoco to complete your payment securely.</p>
                        {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}
                        <button type="submit" id="pay-button" className="pay-now-button" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="animate-spin" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                    REDIRECTING TO SECURE PAYMENT...
                                </>
                            ) : (
                                `PROCEED TO PAY ZAR ${finalTotal.toFixed(2)}`
                            )}
                        </button>
                    </section>
                </div>
                <aside className="checkout-sidebar">
                    <div className="order-summary">
                        <div className="mobile-summary-header" onClick={() => setIsSummaryOpen(!isSummaryOpen)}>
                            <h3>{isSummaryOpen ? 'Hide' : 'Show'} Order Summary</h3>
                            <span className="mobile-summary-total">ZAR {totalAmount}</span>
                        </div>
                        <div className={isSummaryOpen ? "summary-content open" : "summary-content"}>
                            {Object.keys(filteredCartItems).map(key => {
                                const item = filteredCartItems[key];
                                const itemTotal = (item.price || 0) * (item.quantity || 0);
                                return (
                                    <div key={key} className="summary-product">
                                        <div className="summary-product__image-container">
                                            <img src={item.image} alt={item.name} />
                                            <span className="summary-product__quantity">{item.quantity}</span>
                                        </div>
                                        <div className="summary-product__info">
                                            <h4>{item.name}</h4>
                                            <p>Size: {item.size || 'N/A'}</p>
                                        </div>
                                        <p className="summary-product__price">ZAR {itemTotal.toFixed(2)}</p>
                                    </div>
                                );
                            })}
                            {/* Ambassador Code Input */}
                            <div className="ambassador-code-section">
                                <p style={{ fontWeight: '600', marginBottom: '8px', fontSize: '13px', color: '#444' }}>Ambassador / Promo Code</p>
                                {!ambassadorApplied ? (
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            type="text"
                                            placeholder="Enter code (e.g. JOHN1234)"
                                            value={ambassadorInput}
                                            onChange={(e) => setAmbassadorInput(e.target.value.toUpperCase())}
                                            style={{
                                                flex: 1, padding: '9px 12px', border: '1.5px solid #e0e0e0',
                                                borderRadius: '8px', fontSize: '13px', letterSpacing: '1px',
                                                fontWeight: '600', fontFamily: 'monospace', outline: 'none'
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={applyAmbassadorCode}
                                            disabled={ambassadorLoading}
                                            style={{
                                                padding: '9px 14px', background: '#1a1a2e', color: '#fff',
                                                border: 'none', borderRadius: '8px', fontSize: '13px',
                                                fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {ambassadorLoading ? '...' : 'Apply'}
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        background: '#e8f5e9', padding: '10px 14px', borderRadius: '8px',
                                        border: '1px solid #a5d6a7'
                                    }}>
                                        <span style={{ color: '#2e7d32', fontWeight: '600', fontSize: '13px' }}>
                                            ✓ {ambassadorCode} applied ({ambassadorName})
                                        </span>
                                        <button
                                            type="button"
                                            onClick={removeAmbassadorCode}
                                            style={{
                                                background: 'none', border: 'none', color: '#c62828',
                                                cursor: 'pointer', fontWeight: '700', fontSize: '16px'
                                            }}
                                        >✕</button>
                                    </div>
                                )}
                                {ambassadorError && (
                                    <p style={{ color: '#c62828', fontSize: '12px', marginTop: '6px' }}>{ambassadorError}</p>
                                )}
                            </div>
                            <div className="summary-totals" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span>Subtotal:</span>
                                    <span>ZAR {subtotal.toFixed(2)}</span>
                                </div>
                                {discount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#4CAF50' }}>
                                        <span>Promotion Discount:</span>
                                        <span>-ZAR {discount.toFixed(2)}</span>
                                    </div>
                                )}
                                {ambassadorApplied && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#3f51b5' }}>
                                        <span>Ambassador Discount (10%):</span>
                                        <span>-ZAR {finalAmbassadorDiscount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span>Delivery Fee:</span>
                                    <span>ZAR {deliveryFee.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '18px', marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #333' }}>
                                    <span>Total:</span>
                                    <span>ZAR {finalTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            </form>
        </div>
    );
};

export default Payment;